import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import PremiumLockComponent from '../components/PremiumLockComponent';
import { API_BASE_URL } from '../config';
import RecipeCard from '../components/RecipeCard';
import RecipeMenuGroup from '../components/RecipeMenuGroup';
import RecipeModal from '../components/RecipeModal';
import useKitchenPlan from '../hooks/useKitchenPlan';
import PlanningHorizonToggle from '../components/PlanningHorizonToggle';
import PlanDaysPreview from '../components/PlanDaysPreview';
import MealSwapModal from '../components/MealSwapModal';
import HealthMedicalNotice from '../components/HealthMedicalNotice';
import Swal from 'sweetalert2';
import useMenuApproval from '../hooks/useMenuApproval';

const AthleteKitchen = ({ isDarkMode, toggleDarkMode }) => {
    const [loading, setLoading] = useState(true);
    const [hasPremium, setHasPremium] = useState(false);
    const [dietConfig, setDietConfig] = useState(null);
    const [recipes, setRecipes] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [isPaused, setIsPaused] = useState(false); // Diet pause state
    const [targetMember, setTargetMember] = useState('me');
    const [memberInfo, setMemberInfo] = useState({ name: '', photo: null });
    const [isTrainingDay, setIsTrainingDay] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [inviteLink, setInviteLink] = useState('');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [skillLevel, setSkillLevel] = useState('Deneyimli');
    const [selectedDayIndex, setSelectedDayIndex] = useState(0);

    // [R5] Ortak plan/swap/alışveriş/ufuk mantığı (diet+athlete tek kaynak: useKitchenPlan).
    // onPlanLoaded: athlete'e özel gün seçimi/antrenman durumu (plan yüklenince).
    const {
        planningHorizon, showHorizonModal, setShowHorizonModal,
        weekPlan, setWeekPlan, stockMode, shoppingPayload, shoppingItemCount, creatingShoppingList,
        swapState, setSwapState, selectedRecipe, recipeModalOpen, setRecipeModalOpen, selectedPortion,
        chooseHorizon, onHorizonChange, fetchWeeklyPlan,
        handleCreateShoppingList, openSwap, applySwap, handleRecipeClick
    } = useKitchenPlan('athlete', {
        confirmColor: '#ea580c',
        onPlanLoaded: (data) => {
            const plan = data?.weekPlan || [];
            if (plan.length > 0) setPlanDuration(plan.length.toString());
            const todayStr = new Date().toISOString().split('T')[0];
            const todayIdx = plan.findIndex((d) => d.date === todayStr);
            if (todayIdx !== -1) { setSelectedDayIndex(todayIdx); setIsTrainingDay(plan[todayIdx].isTrainingDay); }
            else if (plan.length > 0) setIsTrainingDay(plan[0].isTrainingDay);
        }
    });
    const scrollRef = useRef(null);
    const dayRefs = useRef([]);

    const { approveMenu, isApproving } = useMenuApproval({
        onSuccess: () => {
            fetchWeeklyPlan();
        }
    });

    // Form State for Senaryo B (Auto-calculate)
    const [formData, setFormData] = useState({
        height: '',
        weight: '',
        age: '',
        gender: 'male',
        activityLevel: 'moderate',
        goal: 'maintain'
    });
    const [setupStep, setSetupStep] = useState(1); // 1: Info, 1.5: Duration, 2: Schedule, 3: Approval Journey, 4: Reminder
    const [planDuration, setPlanDuration] = useState('7'); // '7' or '30'
    const [approvalDayIndex, setApprovalDayIndex] = useState(0); // 0-6 for 7-day review
    const [reminderTime, setReminderTime] = useState('10:00');
    const [selectedSchedule, setSelectedSchedule] = useState({}); // { 'YYYY-MM-DD': boolean }

    // Recipe Modal State (selectedRecipe/recipeModalOpen/selectedPortion → useKitchenPlan)
    const [planModalOpen, setPlanModalOpen] = useState(false);
    const [dismissedNoteContent, setDismissedNoteContent] = useState(localStorage.getItem('dismissed_note'));
    const [healthPlusEntitled, setHealthPlusEntitled] = useState(false);
    const [healthPlusActive, setHealthPlusActive] = useState(false);
    const [healthPlusBusy, setHealthPlusBusy] = useState(false);

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('authToken');
                const [statusRes, profileRes, dietRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/profile/status`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${API_BASE_URL}/api/profile`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${API_BASE_URL}/api/athlete/config`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(() => ({ data: { dietConfig: null } }))
                ]);

                setHasPremium(statusRes.data.user?.hasAthletePremium || statusRes.data.hasAthletePremium || false);
                const profile = profileRes.data;
                const userData = statusRes.data.user || {};
                setUserProfile(profile);
                const healthEntitled = !!profile?.entitlements?.modules?.global?.granted
                    && !profile?.entitlements?.lockedByTime;
                setHealthPlusEntitled(healthEntitled);
                try {
                    const healthStateRes = await axios.get(`${API_BASE_URL}/api/health/activation-state`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setHealthPlusActive(healthEntitled && !!healthStateRes.data?.enabled);
                } catch {
                    setHealthPlusActive(healthEntitled);
                }

                // Parse family members from profile
                const members = [{
                    id: 'me',
                    name: userData.name || 'Ben',
                    type: 'me',
                    photo: userData.photo || userData.avatar
                }];

                if (profile.partnerId) {
                    members.push({
                        id: 'partner',
                        name: profile.partnerName || 'Eşim',
                        type: 'partner',
                        photo: profile.partnerAvatar || profile.partnerPhoto
                    });
                }

                if (profile.children) {
                    try {
                        const childrenArr = typeof profile.children === 'string'
                            ? JSON.parse(profile.children)
                            : profile.children;

                        if (Array.isArray(childrenArr)) {
                            childrenArr.forEach((child, index) => {
                                // 1. Dedup: Skip if this child entry is actually the partner
                                if (child.role === 'partner' || child.type === 'partner') return;

                                // 2. Age Restriction: Athlete mode is STRICTLY for 18+ items
                                if (child.birthDate) {
                                    const birthDate = new Date(child.birthDate);
                                    const ageInYears = (new Date() - birthDate) / (1000 * 60 * 60 * 24 * 365);
                                    if (ageInYears < 18) return;
                                } else {
                                    return;
                                }

                                members.push({
                                    id: `child_${index}`,
                                    name: child.name || `Çocuk ${index + 1}`,
                                    type: 'child',
                                    photo: child.photo || child.avatar
                                });
                            });
                        }
                    } catch (e) {
                        console.warn('Failed to parse children:', e);
                    }
                }
                setFamilyMembers(members);

                if (dietRes.data.dietConfig) {
                    const config = dietRes.data.dietConfig;
                    setDietConfig(config);
                    setTargetMember(config.targetMember || 'me');

                    // ✅ Load plan duration from config
                    if (config.effectiveTargets?.planDuration) {
                        setPlanDuration(config.effectiveTargets.planDuration);
                    }

                    const today = new Date().toISOString().split('T')[0];
                    if (config.dailyStates?.[today]) {
                        setIsTrainingDay(config.dailyStates[today].isTrainingDay);
                    }
                } else {
                    setShowMemberModal(true);
                }

                if ((statusRes.data.user?.hasAthletePremium || statusRes.data.hasAthletePremium) && dietRes.data.dietConfig) {
                    await fetchWeeklyPlan(); // ✅ Fetch weekly plan on init

                    // ✅ Load reminder time from KitchenPlanner
                    try {
                        const plannerRes = await axios.get(`${API_BASE_URL}/api/athlete/reminder-time`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        if (plannerRes.data.success && plannerRes.data.planner?.athleteReminderTime) {
                            setReminderTime(plannerRes.data.planner.athleteReminderTime);
                        }
                    } catch (plannerErr) {
                        // Reminder-time endpoint hatası premium erişimi düşürmemeli
                        console.warn('Athlete reminder-time load skipped:', plannerErr?.response?.status || plannerErr?.message);
                    }
                }
            } catch (error) {
                console.error('Initialization error:', error);
                setHasPremium(false);
            } finally {
                setLoading(false);
            }
        };
        initialize();
    }, []);

    useEffect(() => {
        if (userProfile && targetMember) {
            updateMemberDisplayInfo();
        }
    }, [userProfile, targetMember]);

    const updateMemberDisplayInfo = () => {
        if (!userProfile) return;
        if (targetMember === 'me') {
            setMemberInfo({ name: userProfile.user?.name || 'Siz', photo: userProfile.user?.avatar });
        } else if (targetMember === 'partner') {
            setMemberInfo({ name: userProfile.partnerName || 'Eşim', photo: userProfile.partnerAvatar || userProfile.partnerPhoto || null });
        } else if (targetMember.startsWith('child_')) {
            const idx = parseInt(targetMember.split('_')[1]);
            const children = typeof userProfile.children === 'string' ? JSON.parse(userProfile.children) : (userProfile.children || []);
            if (children[idx]) {
                setMemberInfo({ name: children[idx].name, photo: children[idx].photo || children[idx].avatar });
            }
        }
    };

    const toggleTrainingDay = async (date) => {
        const targetDate = date || new Date().toISOString().split('T')[0];
        const newValue = !isTrainingDay;
        setIsTrainingDay(newValue);

        try {
            const token = localStorage.getItem('authToken');
            await axios.post(`${API_BASE_URL}/api/athlete/daily-state`,
                { date: targetDate, isTrainingDay: newValue },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchWeeklyPlan();
        } catch (error) {
            console.error('Failed to save training state:', error);
        }
    };

    const generateInviteLink = async () => {
        setInviteLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.post(`${API_BASE_URL}/api/athlete/invite-link`,
                { targetMember, role: 'pt' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.ok) {
                const link = res.data.invite.link;
                setInviteLink(link);
                const waText = encodeURIComponent(`Şefim selam! BBTV Sporcu Mutfağı için performans hedeflerimi girmene yardımcı olacak linkim hazır: ${link}`);
                const waUrl = `https://wa.me/?text=${waText}`;

                Swal.fire({
                    title: 'PT Linki Hazır!',
                    html: `
                        <p class="mb-4 text-slate-600 dark:text-slate-400">Uzman linke tıkladığında size özel performans hedeflerinizi girebilir.</p>
                        <a href="${waUrl}" target="_blank" class="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-2xl font-bold hover:bg-[#128C7E] transition-all no-underline">
                            <span class="material-symbols-outlined">share</span>
                            WhatsApp'ta Paylaş
                        </a>
                    `,
                    icon: 'success',
                });
            }
        } catch (error) {
            Swal.fire('Hata', 'Link oluşturulamadı.', 'error');
        } finally {
            setInviteLoading(false);
        }
    };

    // ✅ Automatic Plan Renewal
    useEffect(() => {
        if (!weekPlan.length || !dietConfig) return;

        const checkAndRenewPlan = async () => {
            const lastDay = weekPlan[weekPlan.length - 1];
            if (!lastDay) return;

            const lastDate = new Date(lastDay.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            lastDate.setHours(0, 0, 0, 0);

            // If we've reached or passed the last day, fetch new plan
            if (today >= lastDate) {
                console.log('📅 Plan expired, fetching new plan...');
                await fetchWeeklyPlan();
            }
        };

        checkAndRenewPlan();
        // Check daily at midnight
        const interval = setInterval(checkAndRenewPlan, 24 * 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, [weekPlan, dietConfig]);

    // Auto-scroll to active day
    useEffect(() => {
        if (dayRefs.current[selectedDayIndex]) {
            dayRefs.current[selectedDayIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [selectedDayIndex, weekPlan]);

    useEffect(() => {
        if (!weekPlan.length) return;
        const todayIdx = weekPlan.findIndex((day) => day.date === new Date().toISOString().split('T')[0]);
        if (todayIdx >= 0) {
            setSelectedDayIndex(todayIdx);
            setIsTrainingDay(weekPlan[todayIdx]?.isTrainingDay || false);
        }
    }, [planningHorizon, weekPlan]);

    const handleCalculate = async (e) => {
        if (e) e.preventDefault();

        if (setupStep === 1) {
            setSetupStep(1.5);
            return;
        }

        if (setupStep === 1.5) {
            setSetupStep(2);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            // 1. Calculate and save DietConfig
            const res = await axios.post(`${API_BASE_URL}/api/athlete/calculate`,
                { ...formData, mode: 'athlete', targetMember, planDuration },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (res.data.ok) {
                // 2. Save training days selected
                const dayPromises = Object.entries(selectedSchedule).map(([date, isTrainingDay]) => {
                    return axios.post(`${API_BASE_URL}/api/athlete/daily-state`,
                        { date, isTrainingDay },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                });

                await Promise.all(dayPromises);

                setDietConfig(res.data.dietConfig);
                await fetchWeeklyPlan();

                // ✅ Save reminder time to KitchenPlanner
                await axios.post(`${API_BASE_URL}/api/athlete/reminder-time`,
                    { reminderTime, type: 'athlete' },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                // Transition to Approval Journey
                setSetupStep(3);
                Swal.fire({
                    title: 'Planınız Hazırlanıyor!',
                    text: '7 günlük örnek planınızı inceleyin.',
                    icon: 'success',
                    timer: 2000
                });
                setApprovalDayIndex(0);
            }
        } catch (error) {
            console.error('Calculation failed:', error);
            Swal.fire('Hata', 'Plan oluşturulurken bir sorun oluştu.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleApprovalNext = () => {
        if (approvalDayIndex < parseInt(planDuration) - 1) {
            setApprovalDayIndex(prev => prev + 1);
        } else {
            setSetupStep(4); // Move to Reminder
        }
    };

    const handleFinalizePlan = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            // Save reminder time and approve plan
            await axios.post(`${API_BASE_URL}/api/profile/update-athlete-settings`, {
                reminderTime,
                isApproved: true
            }, { headers: { Authorization: `Bearer ${token}` } });

            Swal.fire({
                title: 'Plan Başlatıldı!',
                text: `Şefim, yemeklerini her gün saat ${reminderTime}'de hatırlatacağım.`,
                icon: 'success',
                timer: 3000,
                showConfirmButton: false
            });
            window.location.reload();
        } catch (error) {
            console.error('Finalization failed:', error);
        } finally {
            setLoading(false);
        }
    };


    const handlePlanApproval = async () => {
        // Collect top recommendation for each meal in the whole week
        const topRecipes = weekPlan.flatMap(day => {
            return Object.values(day.meals || {}).map(mealList => mealList[0]).filter(Boolean);
        });

        if (topRecipes.length === 0) {
            Swal.fire('Hata', 'Onaylanacak tarif bulunamadı.', 'error');
            return;
        }

        await approveMenu(topRecipes);
    };

    const handleResetPlan = async () => {
        const result = await Swal.fire({
            title: 'Planı Sıfırla?',
            text: 'Tüm hedefleriniz ve haftalık planınız silinecektir. Emin misiniz?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Evet, Sıfırla',
            cancelButtonText: 'İptal'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('authToken');
                await axios.delete(`${API_BASE_URL}/api/athlete/config`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setDietConfig(null);
                setWeekPlan([]);
                setShowMemberModal(true);
                Swal.fire('Sıfırlandı', 'Planınız başarıyla temizlendi.', 'success');
            } catch (error) {
                console.error('Reset failed:', error);
                Swal.fire('Hata', 'Sıfırlama işlemi başarısız oldu.', 'error');
            }
        }
    };

    const handleGoToPlanSetup = () => {
        setSetupStep(1);
        setDietConfig({ placeholder: true });
    };

    const handleToggleHealthPlus = async () => {
        if (healthPlusBusy) return;
        if (!healthPlusEntitled) {
            Swal.fire('Bilgi', 'Saglik+ aktivasyonu icin global premium gerekir.', 'info');
            return;
        }
        try {
            setHealthPlusBusy(true);
            const token = localStorage.getItem('authToken');
            const res = await axios.post(
                `${API_BASE_URL}/api/health/activation-state`,
                { enabled: !healthPlusActive },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHealthPlusActive(!!res.data?.enabled);
        } catch (error) {
            console.error('athlete health+ toggle error', error);
            Swal.fire('Hata', 'Saglik+ durumu guncellenemedi.', 'error');
        } finally {
            setHealthPlusBusy(false);
        }
    };

    const scrollManual = (direction) => {
        if (direction === 'left' && selectedDayIndex > 0) {
            const newIndex = selectedDayIndex - 1;
            setSelectedDayIndex(newIndex);
            setIsTrainingDay(weekPlan[newIndex]?.isTrainingDay || false);
            // Scroll to the new day
            if (dayRefs.current[newIndex]) {
                dayRefs.current[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        } else if (direction === 'right' && selectedDayIndex < weekPlan.length - 1) {
            const newIndex = selectedDayIndex + 1;
            setSelectedDayIndex(newIndex);
            setIsTrainingDay(weekPlan[newIndex]?.isTrainingDay || false);
            // Scroll to the new day
            if (dayRefs.current[newIndex]) {
                dayRefs.current[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    };

    const handleUpgrade = async () => {
        try {
            const token = localStorage.getItem('authToken');
            await axios.post(`${API_BASE_URL}/api/admin/user-upgrade-diet`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            window.location.reload();
        } catch (error) {
            console.error('Upgrade failed:', error);
            Swal.fire('Hata', 'Abonelik işlemi şu an gerçekleştirilemiyor.', 'error');
        }
    };

    const handlePause = async () => {
        if (isPaused) {
            setIsPaused(false);
            Swal.fire({
                title: 'Denge Planı Aktif',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } else {
            const result = await Swal.fire({
                title: 'Denge Planını Duraklat?',
                html: 'Misafir günü veya özel bir durum mu var?<br><small class="text-slate-500">Tarif önerileri normal devam eder, sadece uyumluluk etiketleri gizlenir.</small>',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Evet, Duraklat',
                cancelButtonText: 'İptal'
            });
            if (result.isConfirmed) {
                setIsPaused(true);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-white dark:bg-[#1a1a1a]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
            </div>
        );
    }

    if (!hasPremium) {
        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                <div className="max-w-4xl mx-auto py-20 px-6">
                    <PremiumLockComponent
                        title="Sporcu Mutfağı"
                        message="Beslenme hedeflerinize mutfağınızdan ulaşın. Kişiselleştirilmiş makro takibi ve denge planını keşfedin."
                        paymentModuleKey="athlete"
                        paymentModuleTitle="Sporcu Mutfagi"
                    />
                </div>
            </DashboardLayout>
        );
    }

    if (!dietConfig) {
        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                {showMemberModal && (
                    <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-2xl w-full p-8">
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-2">Bu Plan Kimin İçin?</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {familyMembers.map((member) => (
                                    <button key={member.id} onClick={() => { setTargetMember(member.id); setShowMemberModal(false); }} className="p-6 bg-slate-50 dark:bg-slate-700/50 rounded-2xl border-2 border-slate-200 dark:border-slate-600 hover:border-green-500 transition-all text-left">
                                        <div className="flex items-center gap-4">
                                            <div className="h-14 w-14 bg-white dark:bg-slate-900 rounded-2xl overflow-hidden flex items-center justify-center border-2 border-slate-200">
                                                {member.photo ? <img src={member.photo} className="w-full h-full object-cover" /> : <span>👤</span>}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-black text-slate-900 dark:text-white">{member.name}</h3>
                                                <p className="text-xs text-slate-500">{member.type === 'me' ? 'Kendin' : 'Aile Üyesi'}</p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {!showMemberModal && !inviteLink && (
                    <div className="max-w-5xl mx-auto p-8">
                        <h1 className="text-4xl font-extrabold text-center mb-4">Performans Planı</h1>
                        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mb-10 max-w-2xl mx-auto">
                            Bu alanda iki farklı akış var: PT ile çalışarak hedeflerinizi uzmanla yönetin veya otomatik hesaplama ile
                            sistemin günlük planı başlatmasını sağlayın.
                        </p>
                        <div className="grid md:grid-cols-2 gap-6">
                            <button onClick={generateInviteLink} className="p-8 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 rounded-[2.5rem] text-left">
                                <h3 className="text-2xl font-black text-blue-900 dark:text-blue-300">PT ile Çalış</h3>
                                <p className="text-blue-700 dark:text-blue-400 text-sm mt-3">Antrenörünüz hedeflerinizi sisteme girsin, planınız otomatik şekillensin.</p>
                            </button>
                            <button onClick={() => setDietConfig({ placeholder: true })} className="p-8 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 rounded-[2.5rem] text-left">
                                <h3 className="text-2xl font-black text-green-900 dark:text-green-300">Otomatik Hesapla</h3>
                                <p className="text-green-700 dark:text-green-400 text-sm mt-3">Ölçülerinizi girin, hedefinize göre günlük makro dengesi hemen oluşsun.</p>
                            </button>
                        </div>
                    </div>
                )}
                {inviteLink && (
                    <div className="max-w-xl mx-auto p-12 text-center space-y-8">
                        <h2 className="text-3xl font-black">PT Linki Hazır</h2>
                        <input readOnly value={inviteLink} className="w-full p-4 bg-slate-100 rounded-2xl text-center font-bold" />
                        <button onClick={() => setInviteLink('')} className="text-slate-400 flex items-center gap-2 mx-auto"><span className="material-symbols-outlined">arrow_back</span> Geri</button>
                    </div>
                )}
            </DashboardLayout>
        );
    }

    if (dietConfig?.placeholder) {
        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                <div className="min-h-[80vh] flex items-center justify-center p-4">
                    <div className="w-full max-w-4xl bg-white dark:bg-[#1a1c2e] rounded-[3rem] shadow-2xl dark:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)] border border-slate-200 dark:border-slate-700/50 overflow-hidden animate-in zoom-in-95 duration-500">
                        <div className="p-8 md:p-12">
                            <button
                                onClick={() => setDietConfig(null)}
                                className="mb-10 flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm font-bold opacity-60 hover:opacity-100"
                            >
                                <span className="material-symbols-outlined text-lg">arrow_back</span>
                                Geri Dön
                            </button>

                            <form onSubmit={(e) => { e.preventDefault(); if (setupStep === 3) handleApprovalNext(); else if (setupStep === 4) handleFinalizePlan(); else handleCalculate(); }} className="space-y-12">
                                {setupStep === 1 && (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                                        {/* Left Column: Personal Info */}
                                        <div className="space-y-8">
                                            <h3 className="text-slate-900 dark:text-white text-xs font-black uppercase tracking-[0.2em] opacity-40 mb-6">KİŞİSEL BİLGİLER</h3>
                                            <div className="grid grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <label className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest pl-1">BOY (CM)</label>
                                                    <input
                                                        required
                                                        type="number"
                                                        placeholder="175"
                                                        className="w-full h-14 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl px-5 text-slate-900 dark:text-white font-bold focus:border-green-500/50 focus:ring-4 focus:ring-green-500/10 transition-all outline-none placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                        value={formData.height}
                                                        onChange={e => setFormData({ ...formData, height: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest pl-1">KİLO (KG)</label>
                                                    <input
                                                        required
                                                        type="number"
                                                        placeholder="70"
                                                        className="w-full h-14 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl px-5 text-slate-900 dark:text-white font-bold focus:border-green-500/50 focus:ring-4 focus:ring-green-500/10 transition-all outline-none placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                        value={formData.weight}
                                                        onChange={e => setFormData({ ...formData, weight: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <label className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest pl-1">YAŞ</label>
                                                    <input
                                                        required
                                                        type="number"
                                                        placeholder="25"
                                                        className="w-full h-14 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl px-5 text-slate-900 dark:text-white font-bold focus:border-green-500/50 focus:ring-4 focus:ring-green-500/10 transition-all outline-none placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                        value={formData.age}
                                                        onChange={e => setFormData({ ...formData, age: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest pl-1">CİNSİYET</label>
                                                    <select
                                                        className="w-full h-14 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl px-5 text-slate-900 dark:text-white font-bold focus:border-green-500/50 transition-all outline-none appearance-none"
                                                        value={formData.gender}
                                                        onChange={e => setFormData({ ...formData, gender: e.target.value })}
                                                    >
                                                        <option value="male">Erkek</option>
                                                        <option value="female">Kadın</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Column: Goals */}
                                        <div className="space-y-8">
                                            <h3 className="text-slate-900 dark:text-white text-xs font-black uppercase tracking-[0.2em] opacity-40 mb-6">HEDEFLER</h3>
                                            <div className="space-y-2">
                                                <label className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest pl-1">AKTİVİTE SEVİYESİ</label>
                                                <select
                                                    className="w-full h-14 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl px-5 text-slate-900 dark:text-white font-bold focus:border-green-500/50 transition-all outline-none appearance-none"
                                                    value={formData.activityLevel}
                                                    onChange={e => setFormData({ ...formData, activityLevel: e.target.value })}
                                                >
                                                    <option value="sedentary">Hareketsiz (Az veya hiç egzersiz)</option>
                                                    <option value="light">Hafif Hareketli (Haftada 1-2 egzersiz)</option>
                                                    <option value="moderate">Orta Hareketli (Haftada 3-4 egzersiz)</option>
                                                    <option value="active">Çok Hareketli (Haftada 5-6 egzersiz)</option>
                                                    <option value="very_active">Profesyonel (Günde 2+ antrenman)</option>
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest pl-1">ANA HEDEF</label>
                                                <select
                                                    className="w-full h-14 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl px-5 text-slate-900 dark:text-white font-bold focus:border-green-500/50 transition-all outline-none appearance-none"
                                                    value={formData.goal}
                                                    onChange={e => setFormData({ ...formData, goal: e.target.value })}
                                                >
                                                    <option value="maintain">Kiloyu Korumak</option>
                                                    <option value="lose">Kilo Vermek (Yağ Yakımı)</option>
                                                    <option value="gain">Kilo Almak (Hacim Kazanma)</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 1.5: Plan Duration */}
                                {setupStep === 1.5 && (
                                    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
                                        <div className="text-center">
                                            <h3 className="text-slate-900 dark:text-white text-xs font-black uppercase tracking-[0.2em] opacity-40 mb-2">ADIM 1.5 / 2</h3>
                                            <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">Plan Süresi Seçimi</h2>
                                            <p className="text-slate-500 dark:text-slate-400 text-sm mt-3 font-medium">Bu bir haftalık bir hedef mi, yoksa 30 günlük bir maraton mu?</p>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto py-8">
                                            <button
                                                type="button"
                                                onClick={() => setPlanDuration('7')}
                                                className={`p-8 rounded-[2.5rem] border-2 transition-all flex flex-col items-center gap-4 ${planDuration === '7' ? 'bg-green-500/10 border-green-500 shadow-xl shadow-green-500/10' : 'bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700/50 hover:border-slate-300 dark:hover:border-slate-500'}`}
                                            >
                                                <span className="material-symbols-outlined text-4xl text-green-500">calendar_view_week</span>
                                                <div className="text-center">
                                                    <h4 className="text-slate-900 dark:text-white font-black text-lg">Haftalık Hedef</h4>
                                                    <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">7 Günlük Performans</p>
                                                </div>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => setPlanDuration('30')}
                                                className={`p-8 rounded-[2.5rem] border-2 transition-all flex flex-col items-center gap-4 ${planDuration === '30' ? 'bg-blue-500/10 border-blue-500 shadow-xl shadow-blue-500/10' : 'bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700/50 hover:border-slate-300 dark:hover:border-slate-500'}`}
                                            >
                                                <span className="material-symbols-outlined text-4xl text-blue-500">event_repeat</span>
                                                <div className="text-center">
                                                    <h4 className="text-slate-900 dark:text-white font-black text-lg">Aylık Maraton</h4>
                                                    <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">30 Günlük Disiplin</p>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {setupStep === 2 && (
                                    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
                                        <div className="text-center">
                                            <h3 className="text-slate-900 dark:text-white text-xs font-black uppercase tracking-[0.2em] opacity-40 mb-2">ADIM 2 / 2</h3>
                                            <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">Antrenman Programınız</h2>
                                            <p className="text-slate-500 dark:text-slate-400 text-sm mt-3 font-medium">Şefim hangi günler antrenman yapacaksınız? İşaretleyin, planınızı ona göre optimize edelim.</p>
                                        </div>

                                        <div className={`grid gap-4 py-8 ${planDuration === '30' ? 'grid-cols-5 md:grid-cols-10' : 'grid-cols-2 md:grid-cols-4 lg:grid-cols-7'}`}>
                                            {Array.from({ length: parseInt(planDuration) }).map((_, i) => {
                                                const date = new Date();
                                                date.setDate(date.getDate() + i);
                                                const dateStr = date.toISOString().split('T')[0];
                                                const dayName = date.toLocaleDateString('tr-TR', { weekday: 'short' });
                                                const isActive = selectedSchedule[dateStr];

                                                return (
                                                    <button
                                                        key={dateStr}
                                                        type="button"
                                                        onClick={() => setSelectedSchedule(prev => ({ ...prev, [dateStr]: !prev[dateStr] }))}
                                                        className={`flex flex-col items-center p-3 md:p-5 rounded-2xl border-2 transition-all ${isActive ? 'bg-green-500 border-green-500 shadow-[0_10px_20px_-5px_rgba(34,197,94,0.4)] scale-105' : 'bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700/50 hover:border-slate-300 dark:hover:border-slate-500'}`}
                                                    >
                                                        <span className={`text-[8px] md:text-[10px] font-black uppercase tracking-widest mb-1 ${isActive ? 'text-white' : 'text-slate-500'}`}>{dayName}</span>
                                                        <span className={`text-base md:text-xl font-black ${isActive ? 'text-white' : 'text-slate-700 dark:text-white/80'}`}>{date.getDate()}</span>
                                                        <span className={`material-symbols-outlined mt-2 md:mt-3 text-sm md:text-lg ${isActive ? 'text-white' : 'text-slate-700'}`}>
                                                            {isActive ? 'bolt' : 'hotel'}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <div className="border-t border-slate-200 dark:border-slate-700/50 pt-8 mt-8">
                                            <div className="flex flex-col items-center gap-4">
                                                <div className="flex items-center gap-2 text-slate-400">
                                                    <span className="material-symbols-outlined text-green-500">notifications_active</span>
                                                    <span className="text-xs font-black uppercase tracking-widest">GÜNLÜK HATIRLATMA SAATİ</span>
                                                </div>
                                                <p className="text-slate-500 text-xs font-medium text-center max-w-md">
                                                    Size her gün planınızı hatırlatmamızı istediğiniz saati seçin. (Sadece bir kez sorulur 😉)
                                                </p>
                                                <div className="relative">
                                                    <input
                                                        type="time"
                                                        value={reminderTime}
                                                        onChange={e => setReminderTime(e.target.value)}
                                                        required
                                                        className="bg-slate-50 dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 rounded-xl px-6 py-3 text-2xl font-black text-slate-900 dark:text-white focus:border-green-500 focus:ring-4 focus:ring-green-500/10 outline-none transition-all text-center w-48"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 3: Approval Journey */}
                                {setupStep === 3 && weekPlan.length > 0 && (
                                    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
                                        <div className="text-center">
                                            <h3 className="text-slate-900 dark:text-white text-xs font-black uppercase tracking-[0.2em] opacity-40 mb-2">ONAY YOLCULUĞU - GÜN {approvalDayIndex + 1} / {planDuration}</h3>
                                            <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">{weekPlan[approvalDayIndex]?.dayName} Menüsü</h2>
                                            <p className="text-slate-500 dark:text-slate-400 text-sm mt-3 font-medium">Bu günkü yemeklerinizi onaylayın, bir sonraki güne geçelim.</p>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-8">
                                            {['breakfast', 'lunch', 'dinner'].map(mealType => (
                                                <div key={mealType} className="space-y-4">
                                                    <div className="flex items-center gap-2 text-slate-400">
                                                        <span className="material-symbols-outlined text-sm">{mealType === 'breakfast' ? 'sunny' : mealType === 'lunch' ? 'lunch_dining' : 'dinner_dining'}</span>
                                                        <span className="text-[10px] font-black uppercase tracking-widest">{mealType === 'breakfast' ? 'KAHVALTI' : mealType === 'lunch' ? 'ÖĞLE' : 'AKŞAM'}</span>
                                                    </div>
                                                    {weekPlan[approvalDayIndex]?.meals?.[mealType]?.map(recipe => (
                                                        <RecipeCard
                                                            key={recipe.id}
                                                            recipe={recipe}
                                                            isDarkMode={isDarkMode}
                                                            onCardClick={() => handleRecipeClick(recipe)}
                                                            isCompact={false}
                                                        />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Step 4: Reminder Time */}
                                {setupStep === 4 && (
                                    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500 max-w-xl mx-auto py-12">
                                        <div className="text-center">
                                            <div className="size-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <span className="material-symbols-outlined text-4xl text-green-500">notifications_active</span>
                                            </div>
                                            <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">Hatırlatıcı Ayarı</h2>
                                            <p className="text-slate-500 dark:text-slate-400 text-sm mt-3 font-medium">Şefim; Size bu planda seçtiğiniz yemekleri her gün saat kaç gibi hatırlatalım?</p>
                                        </div>

                                        <div className="flex justify-center py-8">
                                            <input
                                                type="time"
                                                value={reminderTime}
                                                onChange={e => setReminderTime(e.target.value)}
                                                className="text-6xl font-black bg-transparent border-none text-slate-900 dark:text-white focus:ring-0 text-center w-64"
                                            />
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-20 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-[2rem] font-black text-xl shadow-[0_15px_40px_-10px_rgba(16,185,129,0.3)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-4 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <div className="animate-spin size-6 border-4 border-white/20 border-t-white rounded-full" />
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-2xl">
                                                {setupStep === 1 || setupStep === 1.5 ? 'arrow_forward' :
                                                    setupStep === 2 ? 'analytics' :
                                                        setupStep === 3 ? 'verified' : 'rocket_launch'}
                                            </span>
                                            {setupStep === 1 || setupStep === 1.5 ? 'Sonraki Adım' :
                                                setupStep === 2 ? 'Planımı Oluştur' :
                                                    setupStep === 3 ? 'Günün Menüsünü Onayla' : 'Planı Başlat'}
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    const activeMode = dietConfig?.activeMode || 'diet';
    const activeConfig = dietConfig?.configs?.[activeMode] || {};
    const targets = dietConfig?.effectiveTargets || activeConfig.targets || {};
    const todayKey = new Date().toISOString().split('T')[0];
    const todayPlanIndex = Math.max(0, weekPlan.findIndex((day) => day.date === todayKey));
    const visiblePlanDays = (() => {
        if (!weekPlan.length) return [];
        if (planningHorizon === 'today') {
            return [weekPlan[selectedDayIndex] || weekPlan[todayPlanIndex] || weekPlan[0]].filter(Boolean);
        }
        if (planningHorizon === '3d') {
            return weekPlan.slice(todayPlanIndex, todayPlanIndex + 3);
        }
        return weekPlan.slice(todayPlanIndex, todayPlanIndex + 7);
    })();

    // [B13-UI] Protein-tipi dağılımı (haftalık iskelet): Bakliyat/Tavuk/Et/Balık/Sebze.
    const proteinDistribution = (() => {
        const counts = { 'BAKLİYAT': 0, 'TAVUK': 0, 'ET': 0, 'BALIK': 0, 'SEBZE': 0 };
        (Array.isArray(weekPlan) ? weekPlan : []).forEach((day) => {
            ['lunch', 'dinner'].forEach((slot) => {
                const r = (day?.meals?.[slot] || [])[0];
                const k = r?.mealProteinKey;
                if (k && counts[k] !== undefined) counts[k] += 1;
            });
        });
        return counts;
    })();
    const PROTEIN_META = {
        'BAKLİYAT': { icon: '🫘', label: 'Bakliyat' }, 'TAVUK': { icon: '🍗', label: 'Tavuk' },
        'ET': { icon: '🥩', label: 'Et' }, 'BALIK': { icon: '🐟', label: 'Balık' }, 'SEBZE': { icon: '🥗', label: 'Sebze' }
    };
    const hasProteinDist = Object.values(proteinDistribution).some((v) => v > 0);

    return (
        <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
            {/* Mobile Layout (below md) */}
            <div className="md:hidden min-h-screen pb-32">
                <div className="h-6 w-full"></div>

                {/* Mobile Header */}
                <header className="px-5 mb-6">
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full w-fit">
                            <span className="material-symbols-outlined text-primary text-sm">restaurant</span>
                            <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Sporcu Mutfağı</span>
                        </div>
                        <h1 className="text-3xl font-extrabold leading-tight">
                            Hoş Geldin, <br />
                            <span className="text-primary font-black">{memberInfo.name}</span>
                        </h1>
                    </div>
                    <div className="mt-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex items-start gap-3">
                        <div className="w-2 h-2 mt-1.5 rounded-full bg-primary shrink-0"></div>
                        <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                            <span className="font-bold text-primary">Şefim;</span> Bugün <span className="font-bold text-slate-800 dark:text-slate-200">{isTrainingDay ? 'ANTRENMAN' : 'DİNLENME'}</span> günündesiniz. {isTrainingDay ? 'Sizin için bol enerji ve protein içerikli tarifler hazırladım.' : 'Kas toparlanması için dengeli tarifler sunuyorum.'}
                        </p>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        <button
                            onClick={handleToggleHealthPlus}
                            disabled={healthPlusBusy}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold ${
                                healthPlusActive
                                    ? 'border border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
                                    : 'border border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
                            }`}
                        >
                            <span className="material-symbols-outlined text-sm">health_and_safety</span>
                            {healthPlusActive ? 'Saglik+ Aktif' : 'Saglik+ Pasif'}
                        </button>
                        <button
                            onClick={handleGoToPlanSetup}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                            <span className="material-symbols-outlined text-sm">restart_alt</span>
                            Başa Dön
                        </button>
                        <button
                            onClick={handleResetPlan}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/30"
                        >
                            <span className="material-symbols-outlined text-sm">delete</span>
                            Planı Sıfırla
                        </button>
                    </div>
                </header>

                <div className="px-5 mb-6">
                    <PlanningHorizonToggle
                        value={planningHorizon}
                        onChange={onHorizonChange}
                        mode="athlete"
                    />
                    {/* [B13-UI] Protein-tipi dağılımı (haftalık iskelet) */}
                    {hasProteinDist && (
                        <div className="mt-3 flex flex-wrap items-center gap-1.5">
                            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400 mr-1">Protein dağılımı</span>
                            {Object.entries(proteinDistribution)
                                .filter(([, v]) => v > 0)
                                .map(([key, v]) => (
                                    <span key={key} className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700/50 px-2 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                        <span>{PROTEIN_META[key]?.icon}</span>{PROTEIN_META[key]?.label}<span className="text-orange-500">{v}</span>
                                    </span>
                                ))}
                        </div>
                    )}
                </div>

                {/* [B14-D4] Slot değiştirme (swap) modalı */}
                <MealSwapModal
                    open={swapState.open}
                    onClose={() => setSwapState((s) => ({ ...s, open: false }))}
                    loading={swapState.loading}
                    applying={swapState.applying}
                    alternatives={swapState.alternatives}
                    current={swapState.current}
                    mealType={swapState.mealType || 'lunch'}
                    dateLabel={swapState.date ? new Date(swapState.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : ''}
                    onSelect={applySwap}
                    theme="orange"
                />

                {/* [B14-A] Plan-öncesi mod seçimi modalı (girişte BİR KEZ; sonra toggle ile değişir) */}
                {showHorizonModal && dietConfig && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
                        <div className="w-full max-w-lg rounded-3xl bg-white dark:bg-slate-800 p-6 shadow-2xl border-2 border-blue-200 dark:border-blue-700">
                            <div className="text-center mb-5">
                                <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-blue-500 text-[28px]">fitness_center</span>
                                </div>
                                <h3 className="text-xl font-black text-slate-900 dark:text-white">Planını nasıl kuralım?</h3>
                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">İstediğin zaman değiştirebilirsin.</p>
                            </div>
                            <div className="grid gap-3">
                                <button type="button" onClick={() => chooseHorizon('today')} className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-4 text-left hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all">
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-amber-500 text-[26px]">light_mode</span>
                                        <div>
                                            <p className="font-black text-slate-900 dark:text-white">Günlük</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Bugün dolabındakiyle ne yapabilirsin — alışveriş yok.</p>
                                        </div>
                                    </div>
                                </button>
                                <button type="button" onClick={() => chooseHorizon('7d')} className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-4 text-left hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all">
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-blue-500 text-[26px]">calendar_month</span>
                                        <div>
                                            <p className="font-black text-slate-900 dark:text-white">Haftalık</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">7 günlük program + otomatik alışveriş listesi.</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="px-5 mb-6">
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/40 p-4">
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            {planningHorizon === 'today' && 'Bugün görünümünde antrenman veya dinlenme gününe uygun tarifleri tek ekranda takip edersiniz.'}
                            {planningHorizon === '3d' && '3 günlük akışta kısa dönem plan dengesini görüp hazırlığınızı önceden yapabilirsiniz.'}
                            {planningHorizon === '7d' && '7 günlük planda haftanın tamamını görerek sürdürülebilir bir ritim kurabilirsiniz.'}
                        </p>
                    </div>
                </div>

                {/* Mobile Day Selector */}
                <HealthMedicalNotice
                    enabled={healthPlusActive}
                    targetMemberKey={targetMember || 'me'}
                    className="mb-6"
                />
                {planningHorizon === 'today' && weekPlan.length > 0 && (
                    <section className="mb-8">
                        <div className="flex items-center px-4 mb-3">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">
                                {new Date(weekPlan[selectedDayIndex]?.date).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                            </h2>
                            <div className="ml-auto flex gap-1">
                                <button
                                    onClick={() => scrollManual('left')}
                                    className="w-6 h-6 flex items-center justify-center rounded-full bg-white dark:bg-slate-800 shadow-sm"
                                >
                                    <span className="material-symbols-outlined text-xs">chevron_left</span>
                                </button>
                                <button
                                    onClick={() => scrollManual('right')}
                                    className="w-6 h-6 flex items-center justify-center rounded-full bg-white dark:bg-slate-800 shadow-sm"
                                >
                                    <span className="material-symbols-outlined text-xs">chevron_right</span>
                                </button>
                            </div>
                        </div>
                        <div ref={scrollRef} className="flex overflow-x-auto gap-3 px-5 hide-scrollbar">
                            {weekPlan.map((day, idx) => {
                                const isActive = selectedDayIndex === idx;
                                const isToday = day.date === new Date().toISOString().split('T')[0];
                                const isDayTraining = day.isTrainingDay; // ✅ Training status

                                return (
                                    <button
                                        key={day.date}
                                        ref={el => dayRefs.current[idx] = el}
                                        onClick={() => {
                                            setSelectedDayIndex(idx);
                                            setIsTrainingDay(day.isTrainingDay);
                                        }}
                                        className={`flex-shrink-0 flex flex-col items-center w-16 py-3 rounded-2xl transition-all ${isActive
                                            ? 'bg-primary text-white shadow-lg shadow-primary/30'
                                            : 'bg-white dark:bg-slate-800 text-slate-400 shadow-sm'}`}
                                    >
                                        {/* ✅ Training Day Indicator */}
                                        {isDayTraining && (
                                            <span className={`material-symbols-outlined text-xs mb-1 ${isActive ? 'text-orange-300' : 'text-orange-500'}`}>
                                                fitness_center
                                            </span>
                                        )}

                                        <span className={`text-[10px] font-black uppercase tracking-widest ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                                            {day.dayName.substring(0, 3)}
                                        </span>
                                        <span className={`font-black ${isActive ? 'text-xl text-white' : 'text-lg text-slate-700 dark:text-slate-200'}`}>
                                            {new Date(day.date).getDate()}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {planningHorizon !== 'today' && weekPlan.length > 0 && (
                    <main className="px-5 mb-8">
                        {/* [B14-C3/E] Haftalık eksik malzeme uyarısı — grid ÜSTÜNDE (mobilde ilk görünen). */}
                        {stockMode === 'shop' && (
                            <div className="mb-4 rounded-[2rem] border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-5 dark:border-orange-800 dark:from-orange-900/20 dark:to-amber-900/20">
                                {shoppingItemCount > 0 ? (
                                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                                <span className="material-symbols-outlined">shopping_cart</span>
                                            </div>
                                            <div>
                                                <h3 className="text-base font-black text-orange-900 dark:text-orange-100">Haftalık plan için eksik malzemeler</h3>
                                                <p className="mt-0.5 text-sm font-medium text-orange-700 dark:text-orange-300">
                                                    Dolabında olmayan <b>{shoppingItemCount} kalem</b> tek tıkla Market Listelerim'e eklensin.
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleCreateShoppingList()}
                                            disabled={creatingShoppingList}
                                            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-orange-500/20 transition-all hover:bg-orange-700 disabled:opacity-50"
                                        >
                                            <span className="material-symbols-outlined text-lg">{creatingShoppingList ? 'hourglass_top' : 'add_shopping_cart'}</span>
                                            {creatingShoppingList ? 'Oluşturuluyor...' : 'Alışveriş Listesi Oluştur'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                            <span className="material-symbols-outlined">check_circle</span>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-black text-emerald-900 dark:text-emerald-100">Stok yeterli</h3>
                                            <p className="mt-0.5 text-sm font-medium text-emerald-700 dark:text-emerald-300">Bu haftalık plan için ekstra alışverişe gerek yok.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <PlanDaysPreview
                            days={visiblePlanDays}
                            isDarkMode={isDarkMode}
                            onRecipeClick={handleRecipeClick}
                            showTrainingBadges={true}
                            emptyMessage="Bu görünüm için uygun sporcu planı bulunamadı."
                            onSwap={openSwap}
                            swapTheme="orange"
                        />
                    </main>
                )}

                {/* Mobile Meal Sections */}
                <main className={`px-5 space-y-6 ${planningHorizon !== 'today' ? 'hidden' : ''}`}>
                    {['breakfast', 'lunch', 'dinner'].map(mealType => {
                        const meals = weekPlan[selectedDayIndex]?.meals?.[mealType] || [];
                        const mealTarget = weekPlan[selectedDayIndex]?.mealTargets?.[mealType];

                        let icon, label, iconBgClass, iconTextClass;
                        if (mealType === 'breakfast') {
                            icon = 'wb_sunny';
                            label = 'Kahvaltı';
                            iconBgClass = 'bg-yellow-100 dark:bg-yellow-900/30';
                            iconTextClass = 'text-yellow-600';
                        } else if (mealType === 'lunch') {
                            icon = 'restaurant_menu';
                            label = 'Öğle Yemeği';
                            iconBgClass = 'bg-orange-100 dark:bg-orange-900/30';
                            iconTextClass = 'text-orange-600';
                        } else {
                            icon = 'dark_mode';
                            label = 'Akşam Yemeği';
                            iconBgClass = 'bg-indigo-100 dark:bg-indigo-900/30';
                            iconTextClass = 'text-indigo-600';
                        }

                        return (
                            <section key={mealType}>
                                <div className="flex items-center gap-2 mb-4 px-1">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBgClass} ${iconTextClass}`}>
                                        <span className="material-symbols-outlined">{icon}</span>
                                    </div>
                                    <div>
                                        <h3 className="font-black text-lg">{label}</h3>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                            Hedef: {mealTarget?.calories || 0} kcal
                                        </p>
                                    </div>
                                </div>

                                {meals.length === 0 ? (
                                    <div className="bg-white/40 dark:bg-slate-800/40 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-3xl p-6 flex flex-col items-center justify-center min-h-[140px]">
                                        <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-4xl mb-2">{icon}</span>
                                        <h3 className="font-bold text-slate-500 dark:text-slate-400">{label}</h3>
                                        <span className="text-xs text-slate-400">Hedef: {mealTarget?.calories || 0} kcal</span>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {meals.map((recipe) => (
                                            <RecipeCard
                                                key={recipe.id}
                                                recipe={recipe}
                                                isDarkMode={isDarkMode}
                                                onCardClick={() => handleRecipeClick(recipe)}
                                                isCompact={true}
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </main>

                {/* Mobile Fixed Bottom Bar */}
                {planningHorizon === 'today' && weekPlan.length > 0 && (
                    <div className="fixed bottom-0 left-0 right-0 p-4 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-lg z-50">
                        <div className="bg-slate-900 dark:bg-slate-950 rounded-[2rem] p-4 flex items-center justify-between shadow-2xl">
                            <div className="flex items-center gap-3 pl-2">
                                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                                    <span className="material-symbols-outlined">verified</span>
                                </div>
                                <div>
                                    <p className="text-[11px] font-bold text-white leading-tight">Planı Onaylıyorum</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleResetPlan}
                                    className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                                >
                                    <span className="material-symbols-outlined">refresh</span>
                                </button>
                                <button
                                    onClick={handlePlanApproval}
                                    disabled={isApproving}
                                    className="bg-primary text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-primary/30 active:scale-95 transition-all"
                                >
                                    {isApproving ? '...' : 'PLANI BAŞLAT'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Desktop Layout (md and above) - Keep existing design */}
            <div className="hidden md:block max-w-[1400px] mx-auto p-4 md:p-8 pt-24 md:pt-8 space-y-4 md:space-y-12">
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-1 md:mb-2">
                            <span className="material-symbols-outlined text-green-500 text-xs md:text-sm">restaurant</span>
                            <span className="text-[9px] md:text-xs font-black tracking-widest uppercase text-slate-400">SPORCU MUTFAĞI</span>
                        </div>
                        <h1 className="text-lg md:text-4xl font-extrabold text-slate-900 dark:text-white mb-0.5 md:mb-2">
                            Hoş Geldin, <span className="text-green-500">{memberInfo.name}</span>
                        </h1>
                        <div className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg md:rounded-xl border border-slate-100 dark:border-slate-700 w-fit">
                            <span className="relative flex h-2 w-2 md:h-3 md:w-3">
                                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isTrainingDay ? 'bg-orange-400' : 'bg-green-400'}`}></span>
                                <span className={`relative inline-flex rounded-full h-2 w-2 md:h-3 md:w-3 ${isTrainingDay ? 'bg-orange-500' : 'bg-green-500'}`}></span>
                            </span>
                            <p className="text-[9px] md:text-xs font-black text-slate-700 dark:text-slate-300">
                                {weekPlan[selectedDayIndex]?.date === new Date().toISOString().split('T')[0] ? (
                                    <>
                                        Şefim; Bugün <span className={isTrainingDay ? 'text-orange-500' : 'text-green-500'}>{isTrainingDay ? 'ANTRENMAN' : 'DİNLENME'}</span> günündesiniz.
                                    </>
                                ) : (
                                    <span className="opacity-60">{weekPlan[selectedDayIndex]?.dayName} planını inceliyorsunuz.</span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-3">
                        <button
                            onClick={handleToggleHealthPlus}
                            disabled={healthPlusBusy}
                            className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-xs font-bold shadow-sm ${
                                healthPlusActive
                                    ? 'border border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
                                    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                            }`}
                        >
                            <span className="material-symbols-outlined text-base">health_and_safety</span>
                            {healthPlusActive ? 'Saglik+ Aktif' : 'Saglik+ Pasif'}
                        </button>
                        <button
                            onClick={handleGoToPlanSetup}
                            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-bold text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        >
                            <span className="material-symbols-outlined text-base">restart_alt</span>
                            Başa Dön
                        </button>
                        <button
                            onClick={handleResetPlan}
                            className="inline-flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold text-red-700 shadow-sm hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/30"
                        >
                            <span className="material-symbols-outlined text-base">delete</span>
                            Planı Sıfırla
                        </button>
                        <button onClick={() => setPlanModalOpen(true)} className="p-3 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all">
                            <span className="material-symbols-outlined text-slate-600 dark:text-slate-400">analytics</span>
                        </button>
                    </div>
                </header>

                <PlanningHorizonToggle
                    value={planningHorizon}
                    onChange={onHorizonChange}
                    mode="athlete"
                />

                <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/30 p-4 -mt-2 md:-mt-4">
                    <p className="text-xs md:text-sm font-semibold text-slate-700 dark:text-slate-200">
                        {planningHorizon === 'today' && 'Bugün odağında, seçili güne ait öğünleri hedef kalori ile birlikte izlersiniz.'}
                        {planningHorizon === '3d' && '3 günlük görünüm, kısa dönem antrenman temposuna göre menü akışını toplu görmenizi sağlar.'}
                        {planningHorizon === '7d' && '7 günlük görünümde haftalık disiplininizi korumak için tüm planı tek bakışta değerlendirebilirsiniz.'}
                    </p>
                </div>

                {/* Day Selector - Premium Horizontal Carousel */}
                {planningHorizon === 'today' && weekPlan.length > 0 && (
                    <div className="flex justify-center -mt-2 md:-mt-6">
                        <div className="w-full max-w-[1000px] bg-white dark:bg-slate-900/40 p-1 md:p-2 rounded-2xl md:rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-lg shadow-slate-100 dark:shadow-none flex items-center">

                            {/* Left Arrow */}
                            <button
                                type="button"
                                onClick={() => scrollManual('left')}
                                className="flex flex-shrink-0 size-7 md:size-10 items-center justify-center rounded-full hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-400 transition-colors"
                            >
                                <span className="material-symbols-outlined text-base md:text-2xl">chevron_left</span>
                            </button>

                            <div
                                ref={scrollRef}
                                className="flex items-center gap-1 md:gap-2 overflow-x-auto py-1 md:py-2 no-scrollbar scroll-smooth flex-1"
                            >
                                {weekPlan.map((day, idx) => {
                                    const isActive = selectedDayIndex === idx;
                                    const isToday = day.date === new Date().toISOString().split('T')[0];
                                    const isDayTraining = day.isTrainingDay; // ✅ Training status

                                    return (
                                        <button
                                            key={day.date}
                                            ref={el => dayRefs.current[idx] = el}
                                            onClick={() => {
                                                setSelectedDayIndex(idx);
                                                setIsTrainingDay(day.isTrainingDay);
                                            }}
                                            className={`flex-shrink-0 flex flex-col items-center min-w-[64px] md:min-w-[120px] py-1 md:py-3 px-1.5 md:px-4 rounded-xl md:rounded-[1.8rem] transition-all duration-500 ${isActive
                                                ? 'bg-[#10b981] text-white shadow-md md:shadow-lg shadow-green-500/30'
                                                : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                        >
                                            {/* ✅ Training Day Indicator */}
                                            {isDayTraining && (
                                                <span className={`material-symbols-outlined text-[10px] md:text-sm mb-0.5 ${isActive ? 'text-orange-300' : 'text-orange-500'}`}>
                                                    fitness_center
                                                </span>
                                            )}

                                            <span className={`text-[6px] md:text-[10px] font-black uppercase tracking-widest mb-0.5 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                                                {day.dayName}
                                            </span>
                                            <span className={`text-[10px] md:text-base font-black ${isActive ? 'text-white' : 'text-slate-700 dark:text-slate-200'}`}>
                                                {new Date(day.date).getDate()} {new Date(day.date).toLocaleDateString('tr-TR', { month: 'short' })}
                                            </span>
                                            {isToday && !isActive && (
                                                <div className="mt-0.5 size-1 bg-green-500 rounded-full animate-pulse"></div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Right Arrow */}
                            <button
                                type="button"
                                onClick={() => scrollManual('right')}
                                className="flex flex-shrink-0 size-7 md:size-10 items-center justify-center rounded-full hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-400 transition-colors"
                            >
                                <span className="material-symbols-outlined text-base md:text-2xl">chevron_right</span>
                            </button>
                        </div>
                    </div>
                )}

                {planningHorizon !== 'today' && weekPlan.length > 0 && (
                    <PlanDaysPreview
                        days={visiblePlanDays}
                        isDarkMode={isDarkMode}
                        onRecipeClick={handleRecipeClick}
                        showTrainingBadges={true}
                        emptyMessage="Bu görünüm için uygun sporcu planı bulunamadı."
                    />
                )}

                <div className={`space-y-3 md:space-y-12 ${planningHorizon !== 'today' ? 'hidden' : ''}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 md:gap-8">
                        {['breakfast', 'lunch', 'dinner'].map(mealType => (
                            <div key={mealType} className="space-y-2 md:space-y-6 bg-slate-50/50 dark:bg-slate-900/30 p-2.5 md:p-6 rounded-2xl md:rounded-[2.5rem] border border-slate-100 dark:border-slate-800/50">
                                <div className="flex items-center gap-2 md:gap-3">
                                    <div className="h-7 w-7 md:h-10 md:w-10 rounded-lg md:rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400">
                                        <span className="material-symbols-outlined text-[14px] md:text-xl">{mealType === 'breakfast' ? 'sunny' : mealType === 'lunch' ? 'lunch_dining' : 'dinner_dining'}</span>
                                    </div>
                                    <div>
                                        <h2 className="text-sm md:text-lg font-black text-slate-800 dark:text-white capitalize leading-tight">{mealType === 'breakfast' ? 'Kahvaltı' : mealType === 'lunch' ? 'Öğle Yemeği' : 'Akşam Yemeği'}</h2>
                                        <p className="text-[7px] md:text-[9px] font-bold text-slate-400 leading-none">Hedef: {weekPlan[selectedDayIndex]?.mealTargets?.[mealType]?.calories} kcal</p>
                                    </div>
                                </div>
                                <div className="space-y-1.5 md:space-y-3">
                                    {(weekPlan[selectedDayIndex]?.meals?.[mealType] || []).map((recipe) => (
                                        <RecipeMenuGroup
                                            key={recipe.id}
                                            recipe={recipe}
                                            isDarkMode={isDarkMode}
                                            onCardClick={() => handleRecipeClick(recipe)}
                                            onToggleSelect={() => { }}
                                            selectedRecipeNames={[]}
                                            isCompact={true}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Confirm Entire Week Section */}
                {planningHorizon === 'today' && weekPlan.length > 0 && (
                    <div className="bg-slate-900 dark:bg-slate-100 rounded-2xl md:rounded-[2rem] p-4 md:p-6 shadow-xl flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 overflow-hidden">
                        <div className="flex-1 text-center md:text-left">
                            <h3 className="text-sm md:text-lg font-black text-white dark:text-slate-900 flex items-center gap-2 justify-center md:justify-start">
                                <span className="material-symbols-outlined text-green-500 text-lg md:text-2xl">verified_user</span>
                                {planDuration} Günlük Planı Onayla
                            </h3>
                            <p className="text-slate-400 dark:text-slate-500 text-[10px] md:text-[11px] font-medium">Bu haftalık menü hedeflerinizle tam uyumlu.</p>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4 w-full md:w-auto">
                            <button
                                onClick={handlePlanApproval}
                                disabled={isApproving}
                                className="flex-1 md:flex-none px-4 md:px-8 py-2 md:py-3 bg-green-500 text-white rounded-xl md:rounded-2xl font-black text-[10px] md:text-sm transition-all hover:scale-[1.05] active:scale-95 flex items-center justify-center gap-2"
                            >
                                {isApproving ? <div className="animate-spin size-4 border-2 border-white border-t-transparent rounded-full" /> : 'PLANI BAŞLAT'}
                            </button>
                            <button
                                onClick={handleResetPlan}
                                className="p-2 md:p-3 text-slate-500 hover:text-white dark:hover:text-slate-900 transition-colors"
                                title="Planı Sıfırla"
                            >
                                <span className="material-symbols-outlined text-lg md:text-2xl">restart_alt</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {recipeModalOpen && selectedRecipe && (
                <RecipeModal onPairingClick={handleRecipeClick} isOpen={recipeModalOpen} onClose={() => setRecipeModalOpen(false)} recipe={selectedRecipe} isDarkMode={isDarkMode} userProfile={userProfile} userPortion={selectedPortion} skillLevel={skillLevel} />
            )}

            {planModalOpen && dietConfig && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setPlanModalOpen(false)}>
                    <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 p-4 z-10 shadow-lg flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-black text-white leading-none">Performans Planı</h2>
                                <p className="text-blue-100 text-[10px] font-medium mt-1">Girdiğiniz verilere göre hesaplandı</p>
                            </div>
                            <button onClick={() => setPlanModalOpen(false)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
                                <span className="material-symbols-outlined text-white text-xl">close</span>
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700">
                                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase mb-3 tracking-wider flex items-center gap-2">
                                    <span className="material-symbols-outlined text-sm">nutrition</span>
                                    GÜNLÜK HEDEFLER
                                </p>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-slate-600/50 last:border-0 last:pb-0">
                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">Kalori</p>
                                        <p className="text-sm font-black text-slate-900 dark:text-white">{weekPlan[selectedDayIndex]?.dailyTargets?.calories} <span className="text-[10px] font-normal opacity-60">kcal</span></p>
                                    </div>
                                    <div className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-slate-600/50 last:border-0 last:pb-0">
                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">Protein</p>
                                        <p className="text-sm font-black text-slate-900 dark:text-white">{weekPlan[selectedDayIndex]?.dailyTargets?.protein} <span className="text-[10px] font-normal opacity-60">g</span></p>
                                    </div>
                                    <div className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-slate-600/50 last:border-0 last:pb-0">
                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">Karb</p>
                                        <p className="text-sm font-black text-slate-900 dark:text-white">{weekPlan[selectedDayIndex]?.dailyTargets?.carbs} <span className="text-[10px] font-normal opacity-60">g</span></p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-2xl border border-blue-200/60 dark:border-blue-800/60 bg-blue-50/70 dark:bg-blue-900/20 p-3">
                                <p className="text-[11px] leading-relaxed text-blue-800 dark:text-blue-200">
                                    Bu plan bir tıbbi tedavi önerisi değildir. Performans odaklı beslenme rehberidir; özel sağlık durumları için uzmana danışın.
                                </p>
                            </div>

                            <button onClick={() => setPlanModalOpen(false)} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 px-4 rounded-xl transition-colors text-xs">
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default AthleteKitchen;
